import { useId, useRef, useState } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import { Button, Textarea } from '@/components/ui/primitives.tsx'
import { useAskToLend } from '@/hooks/use-knowledge-bases.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'

/** Where the asker's own last ask about this base, for this agent, stands. */
export type AskState =
  | { kind: 'none' }
  | { kind: 'pending'; at: string }
  | { kind: 'declined'; at: string; by: string | null; note: string; canAskAgain: boolean }

/**
 * "Ask its managers" (PRD-26 D-17), from the agent's page or from the builder.
 *
 * An ask is a request to the base's managers — or to administrators when nobody maintains it —
 * to let the agent answer from the base for everyone who uses it. It grants nothing on its own,
 * and every line here says so: a "sent" that read like "allowed" would leave somebody waiting for
 * answers that may never come.
 *
 * The server keeps one open ask per person, and refuses a repeat inside a day of a "no"; those
 * refusals arrive as states to show rather than failures to apologise for. `already_permitted`
 * means the page was out of date — the reader can use the base after all, or it already answers
 * through this agent — and the caller refetches (`onOutOfDate`).
 *
 * Keyboard: the button opens the form with the note focused; Cancel puts focus back on the button.
 */
export const AskManagers = ({
  baseId,
  baseName,
  agentId,
  agentName,
  ask,
  noMaintainer,
  onOutOfDate
}: {
  baseId: string
  baseName: string
  agentId: string
  agentName: string
  ask: AskState
  noMaintainer: boolean
  onOutOfDate?: () => void
}) => {
  const { formatAgo } = useI18n()
  // Read in the body, never captured at module scope: every sentence below has to follow a
  // locale switch, and a frozen pack fails with no type error and no failing test (F-3). The
  // whole catalogue, because the rate-limit refusal below is rendered out of `errors`.
  const messages = useT()
  const t = messages.marketplace.gallery.ask
  const noteId = useId()
  const titleId = useId()
  const consequenceId = useId()
  const openerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const request = useAskToLend()

  const refusal = request.error instanceof ApiRequestError ? request.error : null
  // Where the ask stands, when that is all there is to say. One status region, mounted before any
  // of it arrives, so "Asked…" is announced rather than silently appearing (GRP-103).
  const standing =
    ask.kind === 'pending'
      ? noMaintainer
        ? t.pendingNoMaintainer(formatAgo(ask.at))
        : t.pending(formatAgo(ask.at))
      : request.isSuccess
        ? noMaintainer
          ? t.sentNoMaintainer
          : t.sent
        : refusal?.code === 'already_permitted'
          ? t.outOfDate
          : refusal?.code === 'conflict'
            ? t.already
            : null
  const status = (
    <p role="status" className="text-[12px] leading-snug text-fg-muted">
      {standing}
    </p>
  )

  const send = () =>
    request.mutate(
      { baseId, agentId, note: note.trim() },
      {
        onError: (error) => {
          if (error instanceof ApiRequestError && error.code === 'already_permitted') onOutOfDate?.()
        }
      }
    )

  const close = () => {
    setOpen(false)
    request.reset()
    // The control that opened the form comes back when it closes; put the reader on it.
    requestAnimationFrame(() => openerRef.current?.focus())
  }

  return (
    <div className="mt-1.5">
      {status}
      {/* Where it stands replaces the controls: nothing is left to press. */}
      {standing === null && (
        <>
          {ask.kind === 'declined' && (
            <div className="mb-1.5 text-[12px] leading-snug text-fg-muted">
              <p>{t.declined(ask.by, formatAgo(ask.at))}</p>
              {ask.note !== '' && <p className="mt-0.5 italic">{t.declinedNote(ask.note)}</p>}
              {!ask.canAskAgain && <p className="mt-0.5">{t.askAgainLater}</p>}
            </div>
          )}

          {!open && (ask.kind === 'none' || (ask.kind === 'declined' && ask.canAskAgain)) && (
            <Button ref={openerRef} size="sm" onClick={() => setOpen(true)}>
              {ask.kind === 'declined' ? t.askAgain : noMaintainer ? t.actionNoMaintainer : t.action}
            </Button>
          )}

          {open && (
            <form
              // Named by its title and described by what allowing it means, so a screen reader
              // announces the form for what it is when the reader lands in its note.
              aria-labelledby={titleId}
              aria-describedby={consequenceId}
              className="rounded-tile border border-rule bg-card-solid px-2.5 py-2 shadow-glass"
              onSubmit={(event) => {
                event.preventDefault()
                send()
              }}
            >
              <p id={titleId} className="text-[12.5px] font-semibold leading-snug">
                {noMaintainer ? t.titleNoMaintainer : t.title(baseName)}
              </p>
              <p id={consequenceId} className="mt-0.5 text-pretty text-[12px] leading-snug text-fg-muted">
                {t.consequence(agentName, baseName)}
              </p>
              {noMaintainer && (
                <p className="mt-0.5 text-pretty text-[12px] leading-snug text-fg-muted">{t.noMaintainer(baseName)}</p>
              )}

              <label htmlFor={noteId} className="mt-2 mb-1 block text-[12px] font-medium">
                {t.noteLabel}
              </label>
              <Textarea
                id={noteId}
                value={note}
                rows={2}
                maxLength={500}
                // Opened by the button right above it, to be typed into.
                autoFocus
                placeholder={t.notePlaceholder}
                className="text-[12.5px]"
                onChange={(event) => setNote(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation()
                    close()
                  }
                }}
              />

              <div className="mt-2 flex items-center gap-2">
                <Button type="submit" size="sm" variant="primary" disabled={request.isPending}>
                  {request.isPending ? t.sending : t.send}
                </Button>
                <Button size="sm" variant="ghost" onClick={close}>
                  {t.cancel}
                </Button>
              </div>

              {/*
                A rate limit is the one refusal here whose wording carries what this dialog cannot
                reconstruct — which day it was refused, which ceiling was hit — so it is the
                server's sentence that is shown. It used to be shown as the server's ENGLISH,
                capitalised on the way past by an asSentence helper, which is why this file was
                the last name on two of the discipline ratchets. The server now names the
                sentence — knowledge.ask_declined_recently, knowledge.too_many_asks — and
                describeApiError renders it from this reader's own catalogue, already a sentence
                and already capitalised by whoever wrote it in that language (D-4, LOC-014).

                The three branches that stay read the machine failure CLASS, never the prose:
                this dialog's own words for "it is gone" and "it no longer applies" beat anything
                the server could write, because only the dialog knows what was on screen.
              */}
              {request.isError && (
                <p role="alert" className="mt-1.5 text-[12px] text-alert">
                  {refusal?.code === 'rate_limited'
                    ? describeApiError(messages, refusal)
                    : refusal?.code === 'not_found'
                      ? t.gone
                      : refusal?.code === 'precondition_failed'
                        ? t.noLongerApplies
                        : t.failed}
                </p>
              )}
            </form>
          )}
        </>
      )}
    </div>
  )
}
